import { uploadApi } from '../../../shared/api/upload.api';
import { exportTemplateToBlob } from '../../../features/template-export/lib/exportTemplate';
import { Template } from '../../../entities/template';
import { MediaFile } from '../model/types';

/**
 * Применяет шаблон к изображению и возвращает File
 */
export async function applyTemplateToImage(
  mediaFile: MediaFile,
  template: Template,
  title: string,
  templateRef: React.RefObject<HTMLDivElement>,
  fitMode?: 'fill' | 'fit',
  // Одиночный пост: холст по аспекту фото (как в превью). Карусель — фикс. 4:5,
  // иначе слайды разной высоты и Instagram обрежет их по первому.
  fitCanvasToMedia = false
): Promise<File> {
  const blob = await exportTemplateToBlob({
    // Шаблон уходит в экспорт целиком. Раньше тут стоял белый список из сорока
    // полей, и он дважды молча терял новые: autoFontMax/bandGrow не доехали до
    // публикации (превью показывало рост плашки, опубликованный пост — нет,
    // отзыв редакции 04.09), а fontVariationSettings не доезжал никогда.
    // exportTemplateToBlob читает только известные ему опции, лишние поля
    // шаблона безвредны. Всё, что публикация осознанно переопределяет, — ниже.
    ...template,
    imageUrl: mediaFile.preview,
    text: title,
    fitMode: fitMode || template.fitMode || 'fill', // Используем переданный fitMode или из шаблона
    fitCanvasToMedia,
    // applyFontEffects=false → пост без обводки/тени/мягкой тени (шрифт/цвет остаются).
    textShadowEnabled: template.applyFontEffects === false ? false : template.textShadowEnabled, // Обводка
    textSoftShadowEnabled: template.applyFontEffects === false ? false : template.textSoftShadowEnabled, // Мягкая тень
    sourceWidth: templateRef.current?.getBoundingClientRect().width,
  });

  // Конвертируем blob в File
  const fileName = mediaFile.file.name.replace(/\.[^/.]+$/, '.png');
  return new File([blob], fileName, { type: 'image/png' });
}

/**
 * Загружает один медиа файл (с или без шаблона)
 */
export async function uploadMediaItem(
  mediaFile: MediaFile,
  options: {
    applyTemplate?: boolean;
    template?: Template;
    title?: string;
    templateRef?: React.RefObject<HTMLDivElement>;
    fitMode?: 'fill' | 'fit';
    fitCanvasToMedia?: boolean;
  }
): Promise<{ url: string; type: 'image' | 'video' }> {
  const { applyTemplate, template, title, templateRef, fitMode, fitCanvasToMedia } = options;

  // Для видео всегда загружаем как есть
  if (mediaFile.type === 'video') {
    const { url } = await uploadApi.uploadVideo(mediaFile.file);
    return { url, type: 'video' };
  }

  // Для изображения: с шаблоном или без
  if (applyTemplate && template && title !== undefined && templateRef) {
    const templateFile = await applyTemplateToImage(mediaFile, template, title, templateRef, fitMode, fitCanvasToMedia);
    const { url } = await uploadApi.uploadImage(templateFile);
    return { url, type: 'image' };
  } else {
    const { url } = await uploadApi.uploadImage(mediaFile.file);
    return { url, type: 'image' };
  }
}

/**
 * Загружает все медиа файлы (первый с шаблоном если нужно)
 */
export async function uploadAllMedia(
  mediaFiles: MediaFile[],
  options: {
    applyTemplateToFirst?: boolean;
    template?: Template;
    title?: string;
    templateRef?: React.RefObject<HTMLDivElement>;
    fitMode?: 'fill' | 'fit';
  }
): Promise<Array<{ url: string; type: 'image' | 'video' }>> {
  const { applyTemplateToFirst, template, title, templateRef, fitMode } = options;

  const uploadPromises = mediaFiles.map((mediaFile, index) => {
    // Применяем шаблон только к первому лементу, если это изображение
    const shouldApplyTemplate =
      index === 0 &&
      applyTemplateToFirst &&
      mediaFile.type === 'image' &&
      template &&
      title !== undefined &&
      templateRef;

    return uploadMediaItem(mediaFile, {
      applyTemplate: shouldApplyTemplate,
      template,
      title,
      templateRef,
      fitMode,
      // Только одиночный пост: у карусели все слайды должны быть одного аспекта.
      fitCanvasToMedia: mediaFiles.length === 1,
    });
  });

  return await Promise.all(uploadPromises);
}
